import React, { useState } from 'react';
import { DOCS_CHART_EXTENSIONS, docsChartUrl, findChartById } from '../docsChartCatalog';

export function DocFigure({ title, caption, children, className = '' }) {
    return (
        <figure
            className={`my-10 overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/60 ${className}`}
        >
            {title ? (
                <figcaption className="border-b border-zinc-100 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-teal-700 dark:border-zinc-800 dark:text-teal-400">
                    {title}
                </figcaption>
            ) : null}
            <div className="p-5 sm:p-7">{children}</div>
            {caption ? (
                <p className="border-t border-zinc-100 px-5 py-3 text-xs leading-relaxed text-zinc-500 dark:border-zinc-800 dark:text-zinc-500">
                    {caption}
                </p>
            ) : null}
        </figure>
    );
}

/**
 * Renders public/docs/charts/{chartId}.webp (falls back to .png if needed).
 */
export function DocChartPlaceholder({ chartId, title, description, suggestedSize, caption }) {
    const meta = findChartById(chartId);
    const displayTitle = title || meta?.title || chartId;
    const displayDesc = description || meta?.description || '';
    const displaySize = suggestedSize || meta?.suggestedSize || '1200 × 675';
    const [extIndex, setExtIndex] = useState(0);
    const [imgFailed, setImgFailed] = useState(false);
    const imageUrl = docsChartUrl(chartId, DOCS_CHART_EXTENSIONS[extIndex]);
    const showImage = !imgFailed && extIndex < DOCS_CHART_EXTENSIONS.length;

    const handleImageError = () => {
        if (extIndex < DOCS_CHART_EXTENSIONS.length - 1) {
            setExtIndex((i) => i + 1);
        } else {
            setImgFailed(true);
        }
    };

    return (
        <figure className="my-10 overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/60">
            <figcaption className="border-b border-zinc-100 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-teal-700 dark:border-zinc-800 dark:text-teal-400">
                {displayTitle}
            </figcaption>
            <div className="p-5 sm:p-7">
                {showImage ? (
                    <img
                        key={imageUrl}
                        src={imageUrl}
                        alt={displayTitle}
                        className="mx-auto max-h-[420px] w-full rounded-xl object-contain"
                        onError={handleImageError}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-zinc-200 bg-zinc-50/90 px-6 py-12 text-center dark:border-zinc-700 dark:bg-zinc-950/40">
                        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-50 text-teal-600 dark:bg-teal-950/50 dark:text-teal-400">
                            <i className="fa-regular fa-image text-2xl" aria-hidden />
                        </span>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                            Diagram coming soon
                        </p>
                        <p className="mt-2 font-mono text-xs text-teal-700 dark:text-teal-400">{chartId}</p>
                        <p className="mt-3 max-w-lg text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                            {displayDesc}
                        </p>
                        <p className="mt-4 text-[11px] text-zinc-400">
                            Suggested size: {displaySize} · WebP preferred ·{' '}
                            <span className="font-mono">public/docs/charts/{chartId}.webp</span>
                        </p>
                    </div>
                )}
            </div>
            {caption ? (
                <p className="border-t border-zinc-100 px-5 py-3 text-xs leading-relaxed text-zinc-500 dark:border-zinc-800 dark:text-zinc-500">
                    {caption}
                </p>
            ) : null}
        </figure>
    );
}

/** @deprecated Docs use DocChartPlaceholder; kept for optional reuse */
export function FlowSteps({ steps }) {
    return (
        <div className="flex flex-col gap-5 sm:flex-row sm:flex-wrap sm:items-stretch sm:justify-center">
            {steps.map((step, index) => (
                <React.Fragment key={step.label}>
                    <div className="flex min-w-[7.5rem] flex-1 flex-col items-center text-center sm:max-w-[9.5rem]">
                        <div
                            className={`flex h-12 w-12 items-center justify-center rounded-xl border text-sm font-bold ${step.tone || 'border-teal-200 bg-teal-50 text-teal-800 dark:border-teal-800 dark:bg-teal-950/50 dark:text-teal-300'}`}
                        >
                            {step.icon ? (
                                <i className={`${step.icon} text-base`} />
                            ) : (
                                index + 1
                            )}
                        </div>
                        <p className="mt-2.5 text-xs font-semibold text-zinc-900 dark:text-white">
                            {step.label}
                        </p>
                        {step.desc ? (
                            <p className="mt-1 text-[10px] leading-snug text-zinc-500 dark:text-zinc-500">
                                {step.desc}
                            </p>
                        ) : null}
                    </div>
                    {index < steps.length - 1 ? (
                        <div className="hidden shrink-0 items-center text-teal-400 sm:flex" aria-hidden>
                            <i className="fa-solid fa-arrow-right text-sm" />
                        </div>
                    ) : null}
                    {index < steps.length - 1 ? (
                        <div className="flex justify-center py-1 sm:hidden" aria-hidden>
                            <i className="fa-solid fa-arrow-down text-teal-400" />
                        </div>
                    ) : null}
                </React.Fragment>
            ))}
        </div>
    );
}

export function StateDiagram({ states, edges }) {
    return (
        <div className="space-y-5">
            <div className="flex flex-wrap justify-center gap-3">
                {states.map((state) => (
                    <div
                        key={state.id}
                        className={`min-w-[5.5rem] rounded-xl border px-4 py-3 text-center text-xs font-semibold ${state.style || 'border-zinc-200 bg-zinc-50 text-zinc-800 dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-zinc-200'}`}
                    >
                        <span className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide opacity-70">
                            {state.id}
                        </span>
                        {state.label}
                    </div>
                ))}
            </div>
            <ul className="space-y-2 rounded-xl border border-zinc-100 bg-zinc-50/80 p-4 text-xs text-zinc-600 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-400">
                {edges.map((edge) => (
                    <li key={`${edge.from}-${edge.to}`} className="flex items-start gap-2">
                        <i className="fa-solid fa-arrow-right-long mt-0.5 text-[10px] text-teal-500" />
                        <span>
                            <strong className="text-zinc-700 dark:text-zinc-300">{edge.from}</strong>
                            {' → '}
                            <strong className="text-zinc-700 dark:text-zinc-300">{edge.to}</strong>
                            {edge.label ? (
                                <span className="text-zinc-500">: {edge.label}</span>
                            ) : null}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function MilestoneTimeline({ milestones }) {
    return (
        <div className="relative pl-7">
            <div
                className="absolute bottom-2 left-[13px] top-2 w-0.5 bg-gradient-to-b from-teal-500 via-teal-400/50 to-zinc-200 dark:to-zinc-700"
                aria-hidden
            />
            <ul className="space-y-5">
                {milestones.map((m, i) => (
                    <li key={m.label} className="relative">
                        <span
                            className={`absolute -left-7 top-0.5 flex h-6 w-6 items-center justify-center rounded-full border-2 text-[10px] font-bold ${
                                m.active
                                    ? 'border-teal-500 bg-teal-500 text-white'
                                    : 'border-zinc-300 bg-white text-zinc-500 dark:border-zinc-600 dark:bg-zinc-900'
                            }`}
                        >
                            {i + 1}
                        </span>
                        <p className="text-sm font-semibold text-zinc-900 dark:text-white">{m.label}</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-zinc-500">{m.desc}</p>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function LayerStack({ layers }) {
    return (
        <div className="mx-auto max-w-md space-y-1">
            {layers.map((layer, i) => (
                <React.Fragment key={layer.label}>
                    <div
                        className={`rounded-xl border px-5 py-3.5 text-center ${layer.style || 'border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/40'}`}
                    >
                        <p className="text-sm font-semibold text-zinc-900 dark:text-white">{layer.label}</p>
                        {layer.items ? (
                            <p className="mt-1 text-[11px] text-zinc-500">{layer.items.join(' · ')}</p>
                        ) : null}
                    </div>
                    {i < layers.length - 1 ? (
                        <div className="flex justify-center py-1 text-teal-500" aria-hidden>
                            <i className="fa-solid fa-chevron-down text-xs" />
                        </div>
                    ) : null}
                </React.Fragment>
            ))}
        </div>
    );
}

export function ComparisonChart({ items }) {
    const max = Math.max(...items.map((i) => i.value), 1);
    return (
        <div className="space-y-5">
            {items.map((item) => (
                <div key={item.label}>
                    <div className="mb-1.5 flex justify-between gap-2 text-xs">
                        <span className="font-medium text-zinc-800 dark:text-zinc-200">{item.label}</span>
                        <span className="shrink-0 text-zinc-500">{item.caption}</span>
                    </div>
                    <div className="h-3 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                        <div
                            className={`h-full rounded-full ${item.color || 'bg-teal-500'}`}
                            style={{ width: `${(item.value / max) * 100}%` }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}

export function RoleDiagram({ roles }) {
    return (
        <div className="grid gap-4 sm:grid-cols-3">
            {roles.map((role) => (
                <div
                    key={role.title}
                    className="rounded-xl border border-zinc-200 bg-gradient-to-b from-white to-zinc-50/80 p-5 dark:border-zinc-700 dark:from-zinc-900 dark:to-zinc-950/50"
                >
                    <span
                        className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${role.iconBg || 'bg-teal-50 text-teal-700 dark:bg-teal-950/50 dark:text-teal-400'}`}
                    >
                        <i className={`${role.icon} text-sm`} />
                    </span>
                    <p className="mt-3 text-sm font-semibold text-zinc-900 dark:text-white">{role.title}</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-zinc-500">{role.desc}</p>
                </div>
            ))}
        </div>
    );
}

export function HubDiagram({ center, spokes }) {
    return (
        <div className="relative mx-auto h-[240px] max-w-sm">
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-32 w-32 rounded-full border-2 border-dashed border-teal-300/60 dark:border-teal-700/50" />
            </div>
            <div className="absolute left-1/2 top-1/2 z-10 flex h-[4.5rem] w-[4.5rem] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-2xl border-2 border-teal-500 bg-teal-50 text-center shadow-md dark:bg-teal-950/60">
                <i className={`${center.icon} text-lg text-teal-700 dark:text-teal-400`} />
                <span className="mt-1 px-1 text-[10px] font-bold leading-tight text-teal-900 dark:text-teal-200">
                    {center.label}
                </span>
            </div>
            {spokes.map((spoke, i) => {
                const angle = (i / spokes.length) * 2 * Math.PI - Math.PI / 2;
                const x = 50 + Math.cos(angle) * 44;
                const y = 50 + Math.sin(angle) * 44;
                return (
                    <div
                        key={spoke.label}
                        className="absolute flex w-[5rem] -translate-x-1/2 -translate-y-1/2 flex-col items-center text-center"
                        style={{ left: `${x}%`, top: `${y}%` }}
                    >
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-200 bg-white text-teal-600 shadow-sm dark:border-zinc-700 dark:bg-zinc-900 dark:text-teal-400">
                            <i className={`${spoke.icon} text-xs`} />
                        </span>
                        <span className="mt-1.5 text-[10px] font-semibold leading-tight text-zinc-700 dark:text-zinc-300">
                            {spoke.label}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
